/**
 * GlobalSearchInline.jsx — app-wide search as a V2 inline dropdown (#14).
 *
 * Renders the topbar search INPUT plus an anchored dropdown directly beneath it
 * (no modal — V2 favors anchored panels). As of Phase B the catalog search is
 * BACKEND-DRIVEN: a debounced call to GET /api/v1/search returns a ranked,
 * permission-filtered, scope-aware result set across agents, skills, policies,
 * connectors, users, orgs, and tenants the caller may read. Recent executions
 * are still surfaced client-side (no global executions endpoint) by prefetching
 * runs for the user's recently-worked-on agents, then filtering by the query.
 *
 * Opens on focus or ⌘K; closes on Esc, outside-click, or after navigating.
 * Empty query shows the NavigationContext recent trail.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, CornerDownLeft, Bot, Wrench, ShieldCheck, Activity, Clock, Loader2,
  Plug, User, Building2, Layers,
} from 'lucide-react';
import { searchGlobal, getExecutions } from '../../services/api';
import { useNavigation } from '../../contexts/NavigationContext';
import { useAuth } from '../../contexts/AuthContext';

function asArray(r) {
  if (Array.isArray(r)) return r;
  if (!r) return [];
  if (Array.isArray(r.data)) return r.data;
  if (Array.isArray(r.items)) return r.items;
  if (r.data && Array.isArray(r.data.items)) return r.data.items;
  return [];
}

const TYPE_META = {
  Agent:     { icon: Bot,         chip: 'Agent',     color: 'text-v2-action-strong',  bg: 'bg-v2-action-soft' },
  Skill:     { icon: Wrench,      chip: 'Skill',     color: 'text-v2-trust-strong',   bg: 'bg-v2-trust-soft' },
  Policy:    { icon: ShieldCheck, chip: 'Policy',    color: 'text-v2-warning-strong', bg: 'bg-v2-warning-soft' },
  Connector: { icon: Plug,        chip: 'Connector', color: 'text-v2-action-strong',  bg: 'bg-v2-action-soft' },
  User:      { icon: User,        chip: 'User',      color: 'text-v2-trust-strong',   bg: 'bg-v2-trust-soft' },
  Org:       { icon: Building2,   chip: 'Org',       color: 'text-v2-ink',            bg: 'bg-v2-surface-muted' },
  Tenant:    { icon: Layers,      chip: 'Tenant',    color: 'text-v2-ink',            bg: 'bg-v2-surface-muted' },
  Execution: { icon: Activity,    chip: 'Execution', color: 'text-v2-ink',            bg: 'bg-v2-surface-muted' },
};

/** No-op kept for backward compatibility with the deprecated palette re-export. */
export function invalidateGlobalSearchCache() { /* backend-driven now; nothing to invalidate */ }

export default function GlobalSearchInline() {
  const navigate = useNavigate();
  const { recentEntities } = useNavigation();
  const { session } = useAuth();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);
  const [hits, setHits] = useState([]);          // backend catalog hits
  const [execItems, setExecItems] = useState([]); // client-prefetched recent runs
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  // ⌘K focuses the input and opens the dropdown.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
        if (inputRef.current) inputRef.current.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Outside-click closes.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // On open: prefetch FRESH recent executions bounded to the user's recent agents.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const recentAgentIds = (recentEntities || [])
      .filter((e) => e.type === 'Agent' && e.id).map((e) => e.id).slice(0, 7);
    if (recentAgentIds.length === 0) { setExecItems([]); return undefined; }
    (async () => {
      const results = await Promise.all(
        recentAgentIds.map((aid) =>
          getExecutions(aid).then((r) => ({ aid, rows: asArray(r) })).catch(() => ({ aid, rows: [] }))));
      if (cancelled) return;
      const items = [];
      for (const { aid, rows } of results) {
        for (const ex of rows.slice(0, 5)) {
          const status = (ex.status || ex.outcome || '').toString();
          const skill = ex.skillName || ex.skill || '';
          const id = ex.id || ex.executionId || ex.envelopeId;
          if (!id) continue;
          const label = skill ? `${skill} run` : `Execution ${String(id).slice(0, 8)}`;
          items.push({
            id, type: 'Execution', label,
            sublabel: [status, String(id).slice(0, 8)].filter(Boolean).join(' · '),
            path: `/agents/${aid}`,
            _search: `${label} ${status} ${skill} execution run`.toLowerCase(),
          });
        }
      }
      setExecItems(items);
    })();
    return () => { cancelled = true; };
  }, [open, recentEntities]);

  // Debounced backend catalog search.
  useEffect(() => {
    const q = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q) { setHits([]); setLoading(false); return undefined; }
    setLoading(true);
    const myReq = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      const r = await searchGlobal(q, {
        limit: 24,
        orgId: session?.orgId,
        tenantId: session?.tenantId,
      }).catch(() => null);
      if (myReq !== reqIdRef.current) return; // a newer keystroke superseded this
      const rows = asArray(r).map((h) => ({
        id: h.id, type: h.type, label: h.label, sublabel: h.sublabel, path: h.path,
      }));
      setHits(rows);
      setLoading(false);
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const recentRows = useMemo(() => (recentEntities || []).map((e) => ({
    id: e.id || e.path, type: TYPE_META[e.type] ? e.type : 'Agent',
    label: e.name, sublabel: 'recent', path: e.path,
  })), [recentEntities]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return recentRows;
    // Backend hits (already ranked) + client-filtered recent executions.
    const execMatches = execItems.filter((x) => x._search.includes(q));
    return [...hits, ...execMatches].slice(0, 28);
  }, [query, hits, execItems, recentRows]);

  useEffect(() => {
    if (selected >= results.length) setSelected(Math.max(0, results.length - 1));
  }, [results.length, selected]);

  useEffect(() => {
    if (!listRef.current) return;
    const row = listRef.current.querySelector(`[data-gs-row="${selected}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const activate = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    setQuery('');
    if (inputRef.current) inputRef.current.blur();
    navigate(item.path);
  }, [navigate]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); if (inputRef.current) inputRef.current.blur(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setSelected((i) => Math.min(i + 1, results.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelected((i) => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') { e.preventDefault(); activate(results[selected]); }
  }, [results, selected, activate]);

  return (
    <div ref={wrapRef} className="relative flex-1 max-w-[480px]">
      <div className={`flex items-center gap-2.5 px-3 h-8 rounded-v2-sm border transition-colors duration-v2 ${open ? 'bg-v2-surface border-v2-border-default' : 'bg-v2-muted border-transparent hover:border-v2-border-default hover:bg-v2-surface'}`}>
        <Search size={14} className="text-v2-ink-muted" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setSelected(0); setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder="Search agents, skills, policies, connectors, users, orgs…"
          spellCheck={false}
          autoComplete="off"
          aria-label="Global search"
          className="flex-1 bg-transparent border-0 outline-none text-[12.5px] text-v2-ink placeholder:text-v2-ink-muted"
        />
        {loading && open
          ? <Loader2 size={12} className="animate-spin text-v2-ink-muted" />
          : <kbd className="text-[10.5px] font-mono-v2 px-1.5 py-px bg-v2-surface text-v2-ink-secondary border border-v2-border-default rounded">⌘K</kbd>}
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 bg-v2-surface border border-v2-border-default rounded-v2-md shadow-xl overflow-hidden">
          <div ref={listRef} className="max-h-[360px] overflow-y-auto p-1.5">
            {!query.trim() && recentRows.length > 0 && (
              <div className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-v2-ink-muted flex items-center gap-1">
                <Clock size={11} /> Recent
              </div>
            )}

            {results.length === 0 && (
              <div className="px-3.5 py-6 text-[12.5px] text-v2-ink-muted text-center">
                {query.trim()
                  ? (loading ? <>Searching…</> : <>No matches for “{query.trim()}”.</>)
                  : <>Type to search agents, skills, policies, connectors, users, orgs, tenants.</>}
              </div>
            )}

            {results.map((item, idx) => {
              const meta = TYPE_META[item.type] || TYPE_META.Agent;
              const Icon = meta.icon;
              const sel = idx === selected;
              return (
                <div
                  key={`${item.type}:${item.id}:${idx}`}
                  data-gs-row={idx}
                  onMouseEnter={() => setSelected(idx)}
                  onMouseDown={(e) => { e.preventDefault(); activate(item); }}
                  className={`flex items-center gap-3 px-2.5 py-2 rounded-v2-sm cursor-pointer ${sel ? 'bg-v2-action-soft' : 'hover:bg-v2-muted'}`}
                >
                  <span className={`w-6 h-6 rounded-v2-sm flex items-center justify-center shrink-0 ${meta.bg} ${meta.color}`}>
                    <Icon size={13} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] font-medium text-v2-ink truncate">{item.label}</span>
                    {item.sublabel && (
                      <span className="block text-[11px] text-v2-ink-muted truncate">{item.sublabel}</span>
                    )}
                  </span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${meta.bg} ${meta.color} shrink-0`}>
                    {meta.chip}
                  </span>
                  {sel && <CornerDownLeft size={13} className="text-v2-action-strong shrink-0" />}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between px-3 py-1.5 border-t border-v2-border-subtle bg-v2-muted text-[10.5px] text-v2-ink-muted">
            <span>↑ ↓ navigate · ↵ jump · esc close</span>
            <span className="text-v2-ink-secondary">Scoped to your access</span>
          </div>
        </div>
      )}
    </div>
  );
}
